import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { brl } from "@/lib/api";
import RaffleCard from "@/components/RaffleCard";
import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck, Zap, Trophy, ArrowRight, Crown, CreditCard, Target, Smartphone, Gift, Clover } from "lucide-react";

export default function Landing() {
  const [raffles, setRaffles] = useState([]);
  const [winners, setWinners] = useState([]);
  const [publicStats, setPublicStats] = useState({ quotas_sold: 0, winners_count: 0, prize_available: 0 });

  useEffect(() => {
    api.get("/raffles?status=active").then(({ data }) => setRaffles(data.slice(0, 6))).catch(() => {});
    api.get("/winners").then(({ data }) => setWinners(data.slice(0, 4))).catch(() => {});
    api.get("/stats/public").then(({ data }) => setPublicStats(data)).catch(() => {});
  }, []);

  const featured = raffles[0];

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass gold-border text-xs uppercase tracking-widest text-amber-200 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Rifa RMC · Premium Edition
            </div>
            <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-50 leading-[1.05]">
              A Rifa da <span className="gold-gradient-text">Região Metropolitana</span> de Campinas.
            </h1>
            <p className="mt-6 text-slate-100 text-lg sm:text-xl max-w-xl leading-relaxed font-medium flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <span>Concorra a prêmios incríveis por apenas <span className="text-amber-200 font-mono font-bold">R$ 0,25</span> por cota</span>
            </p>
            <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-xl leading-relaxed">
              Participe e concorra a kits churrasco, smartphones, itens de tecnologia de ponta e prêmios em PIX diretamente na sua conta.
            </p>
            <ul className="mt-6 space-y-3 max-w-xl text-sm sm:text-base text-slate-200">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-4 h-4 text-amber-200" />
                </span>
                <span>A partir de <span className="text-amber-200 font-mono font-semibold">R$ 0,25</span> por cota</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-emerald-300" />
                </span>
                <span>Pagamento instantâneo via PIX</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-amber-200" />
                </span>
                <span>Sorteios transparentes pela Loteria Federal</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-4 h-4 text-emerald-300" />
                </span>
                <span>Resultados divulgados no Instagram e WhatsApp</span>
              </li>
            </ul>
            <p className="mt-6 text-slate-300 text-sm sm:text-base font-medium max-w-xl flex items-center gap-2">
              <Clover className="w-4 h-4 text-emerald-300" />
              <span>Escolha suas cotas, participe e boa sorte!</span>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/rifas">
                <Button data-testid="hero-cta-comprar" className="gold-btn rounded-full px-6 h-12 text-base">
                  Ver rifas ativas <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/como-funciona">
                <Button variant="outline" className="rounded-full h-12 px-6 border-white/15 bg-transparent text-slate-100 hover:bg-white/5">
                  Como funciona
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat label="Disponíveis em prêmios" value={publicStats.prize_available > 0 ? brl(publicStats.prize_available) : "R$ 30.000,00"} />
              <Stat label="Ganhadores" value={publicStats.winners_count.toLocaleString('pt-BR')} />
              <Stat label="Cotas vendidas" value={publicStats.quotas_sold.toLocaleString('pt-BR')} />
            </div>
          </div>

          <div className="lg:col-span-5">
            {featured && (
              <div className="card-luxury rounded-3xl p-2 fade-up">
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                  {featured.images?.[0] && (
                    <img src={featured.images[0]} alt={featured.title} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                  <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-xs font-mono text-amber-200 uppercase tracking-widest">
                    Rifa em destaque
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="heading text-2xl font-bold text-slate-50">{featured.title}</h3>
                    <p className="text-sm text-slate-300 mt-1">Prêmio: <span className="gold-gradient-text font-bold">{brl(featured.prize_value)}</span></p>
                    <Link to={`/rifa/${featured.raffle_id}`}>
                      <Button className="mt-4 emerald-btn rounded-full w-full h-11" data-testid="hero-featured-cta">
                        Participar por {brl(featured.quota_price)}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
        <Perk icon={<ShieldCheck className="w-5 h-5" />} title="Sorteio Seguro" desc="Todas as rifas são realizadas por sorteio ao vivo, garantindo transparência absoluta." />
        <Perk icon={<Zap className="w-5 h-5" />} title="PIX Instantâneo" desc="Suas cotas ficam ativas em segundos após a confirmação do PIX." />
        <Perk icon={<Trophy className="w-5 h-5" />} title="Prêmios Reais" desc="Entrega assegurada e testemunho público dos ganhadores." />
      </section>

      {/* RAFFLES */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-2">Rifas ativas</p>
            <h2 className="heading text-3xl sm:text-4xl font-bold text-slate-50">Escolha seu <span className="gold-gradient-text">próximo troféu</span></h2>
          </div>
          <Link to="/rifas" className="hidden sm:inline text-sm text-amber-200 hover:text-amber-100 uppercase tracking-widest">Ver todas →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {raffles.map((r) => <RaffleCard key={r.raffle_id} raffle={r} />)}
        </div>
      </section>

      {/* WINNERS STRIP */}
      {winners.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="glass rounded-3xl p-8 border-amber-500/10">
            <div className="flex items-center gap-2 mb-6">
              <Crown className="w-5 h-5 text-amber-300" />
              <h3 className="heading text-xl font-bold text-slate-50">Últimos ganhadores</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {winners.map((w) => (
                <div key={w.raffle_id} className="rounded-2xl border border-white/5 p-4 bg-slate-950/40">
                  <p className="text-xs uppercase tracking-widest text-emerald-300 mb-1">Bilhete #{String(w.winner_ticket || 0).padStart(6, "0")}</p>
                  <p className="text-slate-50 font-semibold">{w.winner_name || "Ganhador RMC"}</p>
                  <p className="text-xs text-slate-400 mt-1">{w.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
    <div className="heading text-xl font-extrabold gold-gradient-text">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
  </div>
);

const Perk = ({ icon, title, desc }) => (
  <div className="card-luxury rounded-2xl p-6">
    <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-200 mb-4">
      {icon}
    </div>
    <h4 className="heading text-lg font-semibold text-slate-50 mb-1">{title}</h4>
    <p className="text-sm text-slate-400">{desc}</p>
  </div>
);
